import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { eventsAPI, membersAPI, unwrapList } from '../../services/api';
import { LoadingPage } from '../../components/Loading';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import './Pos.css';

const regKeys = {
  confirmed: 'events.confirmed',
  pending: 'events.pending',
  cancelled: 'events.cancelled',
};

const EventParticipants = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const volunteer = user?.desk_role === 'volunteer';
  const admin = user?.desk_role === 'admin';
  const { t } = useLanguage();
  const [event, setEvent] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [attendances, setAttendances] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [query, setQuery] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const [eventRes, regRes, attRes] = await Promise.all([
      eventsAPI.adminGet(id),
      eventsAPI.registrations(id),
      eventsAPI.attendances(id),
    ]);
    setEvent(eventRes.data);
    setRegistrations(unwrapList(regRes.data));
    setAttendances(unwrapList(attRes.data).filter((row) => row.status !== 'absent'));
    if (!volunteer) {
      const waitRes = await eventsAPI.adminWaitlist(id);
      setWaitlist(unwrapList(waitRes.data));
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    load()
      .catch(() => {
        if (!cancelled) setMessage('找不到活動');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const registerQuery = async () => {
    const value = query.trim();
    if (!value) return;
    setMessage('');
    try {
      const found = await membersAPI.lookup(value);
      const match = found.data;
      if (match.match !== 'one' || !match.results?.[0]) {
        setMessage(match.match === 'many' ? '多於一位會員，請改用完整編號' : '找不到會員');
        return;
      }
      const response = await eventsAPI.registerMember(id, match.results[0].id);
      setMessage(response.data.message || '報名完成');
      setQuery('');
      await load();
    } catch (err) {
      setMessage(err.response?.data?.message || err.response?.data?.error || '報名失敗');
    }
  };

  const checkInMember = async (memberNumber) => {
    setMessage('');
    try {
      const response = await eventsAPI.checkIn({
        q: memberNumber,
        event_id: id,
        register_and_check_in: false,
      });
      setMessage(response.data.message || '簽到完成');
      await load();
    } catch (err) {
      setMessage(err.response?.data?.message || err.response?.data?.error || '簽到失敗');
    }
  };

  const removeRegistration = async (registrationId) => {
    if (!window.confirm(t('events.confirmDelete'))) return;
    setMessage('');
    try {
      await eventsAPI.deleteRegistration(registrationId);
      setMessage(t('events.deletedReg'));
      await load();
    } catch (err) {
      setMessage(err.response?.data?.error || t('events.deleteRegFail'));
    }
  };

  const removeAttendance = async (attendanceId) => {
    if (!window.confirm(t('events.confirmDelete'))) return;
    setMessage('');
    try {
      await eventsAPI.deleteAttendance(attendanceId);
      setMessage(t('events.deletedAtt'));
      await load();
    } catch (err) {
      setMessage(err.response?.data?.error || t('events.deleteAttFail'));
    }
  };

  if (loading) return <LoadingPage message={t('events.loading')} />;
  if (!event) return <div className="pos-error">{message || t('events.missing')}</div>;

  return (
    <div className="pos-card">
      <p>
        <Link to="/admin/events">{t('events.back')}</Link>
        {!volunteer && (
          <>
            {' · '}
            <Link to={`/admin/events/${id}/edit`}>{t('events.edit')}</Link>
          </>
        )}
      </p>
      <h1>{event.title}</h1>
      {!volunteer && (
        <button type="button" className="pos-btn secondary" onClick={async () => {
          const response = await eventsAPI.exportEvent(id);
          const url = URL.createObjectURL(response.data);
          const link = document.createElement('a');
          link.href = url;
          link.download = `event-${id}.xlsx`;
          link.click();
        }}> {t('events.export')}</button>
      )}
      <p className="pos-hint">{t('events.hint')}</p>

      <section className="pos-section">
        <h2>{t('events.registrants')}</h2>
        <input
          className="pos-scan-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('events.searchPh')}
          aria-label={t('events.registrants')}
          onKeyDown={(e) => {
            if (e.key === 'Enter') registerQuery();
          }}
        />
        <div className="pos-actions">
          <button type="button" className="pos-btn" onClick={registerQuery}>{t('events.registrants')}</button>
        </div>
        {message && <p className="pos-hint">{message}</p>}
      </section>

      <section className="pos-section">
        <h2>{t('events.participants')}</h2>
        {registrations.length === 0 ? (
          <p>{t('events.none')}</p>
        ) : (
          <ul className="pos-list">
            {registrations.map((row) => {
              const member = row.member_details || {};
              const attendance = attendances.find((item) => item.member === row.member);
              return (
                <li key={row.id}>
                  <div>{member.chinese_name || t('events.member')} · {member.member_number || row.member}</div>
                  <div>{t(regKeys[row.status] || '') || row.status}{attendance ? ` · ${t('events.checked')}` : ''}</div>
                  {row.status === 'confirmed' && !attendance && member.member_number && (
                    <button type="button" className="pos-btn" onClick={() => checkInMember(member.member_number)}>
                      {t('events.checkInOne')}
                    </button>
                  )}
                  {!volunteer && admin && (
                    <div className="pos-actions">
                      <button type="button" className="pos-btn secondary" onClick={() => removeRegistration(row.id)}>{t('events.deleteReg')}</button>
                      {attendance && (
                        <button type="button" className="pos-btn secondary" onClick={() => removeAttendance(attendance.id)}>{t('events.deleteAtt')}</button>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>
      {!volunteer && (
        <section className="pos-section">
          <h2>{t('events.waitlist')}</h2>
          <ul className="pos-list">
            {waitlist.map((row) => (
              <li key={row.id}>
                {row.chinese_name} · {row.member_number}
                <button type="button" className="pos-btn" onClick={async () => {
                  await eventsAPI.promoteWaitlist(row.id);
                  setMessage(t('events.promoted'));
                  load();
                }}>{t('events.promote')}</button>
              </li>
            ))}
            {waitlist.length === 0 && <li>{t('events.noWait')}</li>}
          </ul>
        </section>
      )}
    </div>
  );
};

export default EventParticipants;
